import { yupResolver } from '@hookform/resolvers/yup';
import { Workspace } from '@sr-sdks/api-sdk-axios';
import { Form, sonner } from '@sr/ui-library';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useUpdateWorkspaceDetails } from '../../api/workspaces/workspaceApi';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import { useEntityDetailsLocked } from '../../hooks/useEntityDetailsLocked';
import {
  setDetailsDirty,
  setDetailsPending,
} from '../../reducers/workspaceSettingsUI/workspaceSettingsUISlice';
import { useAppDispatch } from '../../stateStore';
import { showOnboardingInvalidFormToast } from '../../utils/helpers/onboardingHelpers';
import { ApiError } from '../../utils/httpError';
import { WorkspaceDetailsAddressSection } from './WorkspaceDetailsAddressSection';
import { WorkspaceDetailsContactSection } from './WorkspaceDetailsContactSection';
import { WorkspaceDetailsEntitySection } from './WorkspaceDetailsEntitySection';
import { workspaceToEntityFormDefaults } from './shared/defaults';
import { getMembershipDetailErrors } from './shared/membershipReadiness';
import {
  workspaceDetailsEntitySchema,
  workspaceDetailsLockedSchema,
  workspaceDetailsOnboardingSchema,
} from './shared/schema';
import { toUpdateWorkspaceDetailsDto } from './shared/submitMapper';
import { WorkspaceDetailsFormValues } from './shared/types';
import { useWorkspaceDetailsFormDependencies } from './shared/useWorkspaceDetailsFormDependencies';

const { toast } = sonner;

const FOCUSABLE_FIELD = 'input, button, select, textarea';

interface WorkspaceDetailsFormProps {
  /**
   * Rendered inside the form element, below the sections. Onboarding puts its
   * Save & Continue button here so it sits within the FormProvider tree and can
   * read formState; the settings page uses the PageHeader instead and omits it.
   */
  footer?: ReactNode;
  /**
   * Onboarding step 2 (APP-2237). Makes a workspace category required, seeds
   * the address country, posts the full payload with `isOnboardingPage`, and
   * swaps in onboarding toast copy. Settings leaves it unset.
   */
  isOnboarding?: boolean;
  /** Settings' Highlight Empty Details: flag what Apply still needs (APP-2386). */
  onHighlightEmptyDetailsReady?: (highlightFn: () => void) => void;
  onResetReady?: (resetFn: () => void) => void;
  /**
   * Called after a successful save, and awaited: onboarding advances the flow
   * here, and the work it does belongs to the same submit as far as the form's
   * error handling is concerned.
   */
  onSaveSuccess?: () => Promise<void> | void;
  workspace: Workspace;
}

/**
 * Unified form container for the MSR3 Workspace Settings page.
 * Owns the single React Hook Form instance shared across all detail sections
 * (entity now; contact + address in Phase 2/3). Submits via one PATCH to
 * /workspaces/:id/details, matching the legacy single-call behaviour.
 *
 * FR-016: the Save button in WorkspaceSettingsLayout links here via
 * form="workspace-details-form". No submit button lives inside any section.
 */
export const WorkspaceDetailsForm = ({
  footer,
  isOnboarding = false,
  onHighlightEmptyDetailsReady,
  onResetReady,
  onSaveSuccess,
  workspace,
}: WorkspaceDetailsFormProps) => {
  const updateMutation = useUpdateWorkspaceDetails();
  const dispatch = useAppDispatch();
  const isEntityDetailsLocked = useEntityDetailsLocked(workspace);
  // Address Search is uncontrolled (must not dirty the form). Bump this key on
  // save/Discard so the input remounts empty.
  const [addressSearchResetKey, setAddressSearchResetKey] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  // Bumped per highlight so the scroll runs after the flagged fields render.
  const [highlightRequest, setHighlightRequest] = useState(0);

  const form = useForm<WorkspaceDetailsFormValues>({
    defaultValues: workspaceToEntityFormDefaults(workspace, { isOnboarding }),
    mode: 'onChange',
    /**
     * Locked wins over onboarding: the lock applies there too, and onboarding's
     * schema is the stricter of the two (it also requires a category), so
     * checking it first would leave the deadlock in place on step 2.
     */
    resolver: yupResolver(
      isEntityDetailsLocked
        ? workspaceDetailsLockedSchema
        : isOnboarding
          ? workspaceDetailsOnboardingSchema
          : workspaceDetailsEntitySchema,
    ),
  });

  const { businessNumberCountry, entityType } =
    useWorkspaceDetailsFormDependencies(form, { isOnboarding, workspace });

  // FR-017: sync dirty/pending/valid state to Redux — WorkspaceSettingsLayout reads from the store.
  // Onboarding's submit button lives in its own page footer and reads formState
  // directly, so it has no use for this and must not leave state behind.
  useEffect(() => {
    if (isOnboarding) return;

    dispatch(setDetailsDirty(form.formState.isDirty));

    return () => {
      dispatch(setDetailsDirty(false));
    };
  }, [form.formState.isDirty, dispatch, isOnboarding]);

  useEffect(() => {
    if (isOnboarding) return;

    dispatch(setDetailsPending(updateMutation.isPending));

    return () => {
      dispatch(setDetailsPending(false));
    };
  }, [updateMutation.isPending, dispatch, isOnboarding]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable in RHF v7
  useEffect(() => {
    onResetReady?.(() => {
      form.reset();
      setAddressSearchResetKey((key) => key + 1);
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable in RHF v7
  useEffect(() => {
    onHighlightEmptyDetailsReady?.(() => {
      const missingDetailErrors = getMembershipDetailErrors(form.getValues());

      // Nothing missing here means unsaved edits, or the saved workspace changed since load.
      if (missingDetailErrors.length === 0) {
        if (form.formState.isDirty) {
          toast.info('Save your changes', {
            description:
              'Your workspace details are filled in. Click Update Details to save them.',
          });
        } else {
          toast.info('Refresh the page', {
            description:
              'Your saved workspace details have changed since this page loaded.',
          });
        }

        return;
      }

      missingDetailErrors.forEach(({ message, name }) =>
        form.setError(name, { message, type: 'required' }),
      );
      setHighlightRequest((request) => request + 1);
    });
  }, []);

  useEffect(() => {
    if (highlightRequest === 0) return;

    // DOM order is top-to-bottom order, so the first flagged control is the
    // top-most one.
    const topMost = formRef.current?.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );

    // Some flagged controls wrap their input (the business number).
    const field = topMost?.matches(FOCUSABLE_FIELD)
      ? topMost
      : (topMost?.querySelector<HTMLElement>(FOCUSABLE_FIELD) ?? topMost);

    // Scroll only the layout's <main>: scrollIntoView also scrolls the page itself
    // near the bottom, and body's overflow: hidden leaves it stuck out of place.
    const contentArea = topMost?.closest('main');

    if (topMost && contentArea) {
      const box = topMost.getBoundingClientRect();
      const view = contentArea.getBoundingClientRect();

      contentArea.scrollTo({
        behavior: 'smooth',
        top:
          contentArea.scrollTop +
          box.top -
          view.top -
          (view.height - box.height) / 2,
      });
    }

    field?.focus({ preventScroll: true });
  }, [highlightRequest]);

  const onInvalid = (errors: FieldErrors<WorkspaceDetailsFormValues>) => {
    const collectMessages = (obj: unknown): string[] => {
      if (!obj || typeof obj !== 'object') return [];
      const record = obj as Record<string, unknown>;

      if (typeof record.message === 'string') return [record.message];

      return Object.values(record).flatMap(collectMessages);
    };

    if (isOnboarding) {
      showOnboardingInvalidFormToast();

      return;
    }

    const messages = Object.values(errors).flatMap(collectMessages);

    toast.error(
      messages.length > 0
        ? messages.join(' · ')
        : 'Please fix the invalid fields before saving.',
    );
  };

  const onSubmit = async (values: WorkspaceDetailsFormValues) => {
    try {
      await updateMutation.mutateAsync({
        id: workspace._id,
        updateWorkspaceDetailsDto: toUpdateWorkspaceDetailsDto(
          values,
          form.formState.dirtyFields,
          workspace.addresses ?? [],
          { isOnboarding },
        ),
      });
      // Onboarding is the first time these details are entered, so nothing has
      // been "updated" yet.
      toast.success(
        isOnboarding
          ? 'Your Workspace details have been saved.'
          : 'Your Workspace details have been updated.',
      );
      // form.reset sets a new baseline → formState.isDirty becomes false
      // → setDetailsDirty dispatches false → PageHeader Save button returns to disabled.
      form.reset(values);
      setAddressSearchResetKey((key) => key + 1);
      // Awaited so a rejection reaches the catch below rather than escaping as
      // an unhandled rejection while the user is told the save succeeded.
      await onSaveSuccess?.();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to save entity details. Please try again.';

      toast.error(message);
      // No reset on failure: dirty state stays true → button stays enabled for retry (FR-011).
    }
  };

  return (
    <Form {...form}>
      {/*
       * FR-016: id="workspace-details-form" links this form to the PageHeader Save button
       * via <Button form="workspace-details-form" type="submit"> — no submit button inside.
       * space-y-8 matches the section gap used by Details.tsx for visual consistency.
       */}
      <form
        id="workspace-details-form"
        className="space-y-8"
        noValidate
        ref={formRef}
        onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      >
        {/*
         * Onboarding only: leaving the step drops the edits, so warn on both
         * in-app navigation and a refresh/close. The settings page has its own
         * Save/Discard affordances in the header and is left alone.
         */}
        {isOnboarding && (
          <UnsavedChangesPrompt hasUnsavedChanges={form.formState.isDirty} />
        )}

        <WorkspaceDetailsEntitySection
          businessNumberCountry={businessNumberCountry}
          entityType={entityType}
          form={form}
          isLocked={isEntityDetailsLocked}
          isOnboarding={isOnboarding}
          workspace={workspace}
        />
        <WorkspaceDetailsContactSection form={form} workspace={workspace} />

        <WorkspaceDetailsAddressSection
          addressSearchResetKey={addressSearchResetKey}
          form={form}
        />

        {footer}
      </form>
    </Form>
  );
};
